import axios from 'axios';
import { useEffect, useState } from 'react';
import type { LoginResponse } from '~entities/users';

import { decryptUserAPI } from '~entities/users/api/decrypt-user';

// Types for GHL user context
export interface GHLUserData {
  userId: string;
  companyId: string;
  role: string;
  activeLocation: string;
  userName: string;
  email: string;
  isAgencyOwner: boolean;
}

export interface GHLUserTokenResponse extends LoginResponse {
  activeLocation: string;
}

interface MessageHandlerData {
  message: string;
  payload: string;
}

interface UseGHLUserContextReturn {
  encryptedUserData: string | null;
  result: GHLUserTokenResponse | null;
  isFetching: boolean;
  error: string | null;
}

export const useGHLUserContext = (): UseGHLUserContextReturn => {
  const [encryptedUserData, setEncryptedUserData] = useState<string | null>(
    null,
  );
  const [userContextDecryptionResponse, setUserContextDecryptionResponse] =
    useState<GHLUserTokenResponse | null>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Effect to get encrypted user data from parent window
  useEffect(() => {
    let messageHandler: ((event: MessageEvent) => void) | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    const cleanup = () => {
      if (messageHandler) {
        window.removeEventListener('message', messageHandler);
        messageHandler = null;
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    async function getEncryptedUserData() {
      try {
        setIsFetching(true);
        setError(null);

        const encryptedData = await new Promise<string | null>((resolve) => {
          // Request user data from parent window
          window.parent.postMessage({ message: 'REQUEST_USER_DATA' }, '*');

          console.log('REQUEST_USER_DATA sent');

          timeoutId = setTimeout(() => {
            cleanup();
            resolve(null);
          }, 10000); // 10 second timeout

          // Listen for the response
          messageHandler = ({ data }: { data: MessageHandlerData }) => {
            if (data.message === 'REQUEST_USER_DATA_RESPONSE') {
              window.removeEventListener(
                'message',
                messageHandler as (event: MessageEvent) => void,
              );
              console.log(
                'REQUEST_USER_DATA_RESPONSE',
                JSON.stringify(data.payload),
              );
              resolve(data.payload);
            }
          };

          window.addEventListener('message', messageHandler);
        });

        if (timeoutId) clearTimeout(timeoutId);

        if (encryptedData) {
          setEncryptedUserData(encryptedData);
        } else {
          setError('Unable to connect to your account. Please refresh the page and try again.');
          setIsFetching(false);
        }
      } catch (err) {
        console.error('Error in getEncryptedUserData', err);
        setError('Something went wrong while loading your account. Please refresh the page and try again.');
        setIsFetching(false);
      }
    }

    void getEncryptedUserData();

    // Cleanup function for useEffect
    return cleanup;
  }, []); // Empty dependency array - runs only once on mount

  // Effect to decrypt user data when encrypted data is available
  useEffect(() => {
    async function decryptUserData() {
      if (!encryptedUserData) {
        return;
      }

      try {
        setIsFetching(true);
        setError(null);

        const decrypted = await decryptUserAPI(encryptedUserData);
        setUserContextDecryptionResponse(decrypted);
      } catch (decryptError) {
        console.error('Error decrypting user data', decryptError);
        const fallback = 'Something went wrong while signing you in. Please try again or contact your studio for help.';
        if (axios.isAxiosError(decryptError)) {
          const msg = decryptError.response?.data?.message;
          setError(typeof msg === 'string' ? msg : fallback);
        } else {
          setError(fallback);
        }
      } finally {
        setIsFetching(false);
      }
    }

    void decryptUserData();
  }, [encryptedUserData]);

  return {
    encryptedUserData,
    result: userContextDecryptionResponse,
    isFetching,
    error,
  };
};
